import { publish } from './pubsub';
import { PUB_SUB_EVENTS } from './constants';
import fetchConfig from './fetch-config';

type SectionsPayload = Record<string, string>;

class CartDiscountCode extends HTMLElement {
  constructor() {
    super();

    // Button click
    this.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const removeBtn = target.closest<HTMLButtonElement>('button[data-remove-discount]');

      if (removeBtn) {
        event.stopPropagation();

        if ('stopImmediatePropagation' in event) {
          (event as Event & { stopImmediatePropagation: () => void }).stopImmediatePropagation();
        }

        event.preventDefault();

        const code = removeBtn.dataset.removeDiscount;
        if (code) {
          this.removeDiscount(code);
        }

        return;
      }

      const applyBtn = target.closest<HTMLButtonElement>('button[id$="-ApplyDiscount"]');

      if (applyBtn) {
        event.stopPropagation();

        if ('stopImmediatePropagation' in event) {
          (event as Event & { stopImmediatePropagation: () => void }).stopImmediatePropagation();
        }

        event.preventDefault();
        this.applyOrRemoveFromInput();
      }
    });

    // Enter on input
    this.addEventListener('keydown', (event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target instanceof HTMLInputElement && /-DiscountCode$/.test(target.id) && event instanceof KeyboardEvent) {
        if (event.key === 'Enter') {
          event.stopPropagation();
          if ('stopImmediatePropagation' in event) {
            (event as KeyboardEvent & { stopImmediatePropagation: () => void }).stopImmediatePropagation();
          }
          event.preventDefault();
          this.applyOrRemoveFromInput();
        }
      }
    });

    // Auto-capitalize discount code as user types
    this.addEventListener('input', (event) => {
      const target = event.target as HTMLInputElement | null;
      if (!target || !(target instanceof HTMLInputElement)) return;
      if (!/-DiscountCode$/.test(target.id)) return;
      const start = target.selectionStart ?? target.value.length;
      const end = target.selectionEnd ?? target.value.length;
      const upper = target.value.toUpperCase();
      if (target.value !== upper) {
        target.value = upper;
        // restore caret position when possible
        try {
          target.setSelectionRange(start, end);
        } catch {
          // do nothing
        }
      }
    });
  }

  private getEls() {
    const input = this.querySelector('input[id$="-DiscountCode"]') as HTMLInputElement | null;
    const button = this.querySelector('button[id$="-ApplyDiscount"]') as HTMLButtonElement | null;
    const message = this.querySelector('p[id$="-DiscountMessage"]') as HTMLParagraphElement | null;
    return { input, button, message };
  }

  private setMessage(text: string) {
    const { message } = this.getEls();
    if (message) message.textContent = text;
  }

  private setMessageOnAllWrappers(text: string) {
    const nodes = document.querySelectorAll('cart-discount-code p[id$="-DiscountMessage"]');
    nodes.forEach((el) => {
      (el as HTMLParagraphElement).textContent = text;
    });
  }

  private getFooterSectionId(): string {
    const footer = document.getElementById('main-cart-footer');
    return footer?.getAttribute('data-id') || '';
  }

  private buildSectionsParam(): string {
    const footerSectionId = this.getFooterSectionId();
    const sections = [
      'cart-drawer',
      'cart-icon-bubble',
      'custom-main-cart-items',
      'custom-main-cart-footer',
      footerSectionId,
    ]
      .filter(Boolean)
      .join(',');
    return sections;
  }

  private getAppliedDiscountCodes(): string[] {
    const codes = Array.from(this.querySelectorAll<HTMLElement>('[data-discount-code]'))
      .map((el) => el.dataset.discountCode?.trim().toUpperCase())
      .filter((code): code is string => Boolean(code));

    return [...new Set(codes)];
  }

  private applyOrRemoveFromInput() {
    const { input } = this.getEls();
    if (!input) return;

    const code = input.value.toUpperCase().trim();

    if (!code) {
      this.setMessage(window.cartStrings.enter_discount_code);
      return;
    }

    this.applyDiscount(code);
  }

  private async applyDiscount(code: string) {
    const { input, button } = this.getEls();
    if (!input || !button) return;

    try {
      button.disabled = true;
      this.setMessage(window.cartStrings.applying_discount);

      const cfg = fetchConfig('javascript') as ReturnType<typeof fetchConfig> & {
        headers: Record<string, string>;
        body?: FormData;
      };
      const headers = cfg.headers as Record<string, string>;
      headers['X-Requested-With'] = 'XMLHttpRequest';
      delete headers['Content-Type'];

      const existingCodes = this.getAppliedDiscountCodes();
      const allCodes = [...new Set([...existingCodes, code])];

      const form = new FormData();
      form.append('discount', allCodes.join(','));
      form.append('sections', this.buildSectionsParam());
      form.append('sections_url', window.location.pathname);
      cfg.body = form;

      const resp = await fetch(`${window.routes.cart_update_url}`, cfg as RequestInit);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const text = await resp.text();
      let parsed:
        | { sections?: SectionsPayload; discount_codes?: Array<{ code?: string; applicable: boolean }> }
        | undefined;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error('Invalid response format');
      }

      const ok = Array.isArray(parsed?.discount_codes)
        ? parsed!.discount_codes!.some((d) => d.code?.toUpperCase().trim() === code && d.applicable !== false)
        : false;

      // Update the messages
      if (ok) {
        if (document.body.contains(this)) {
          input.value = '';
          input.removeAttribute('readonly');
        }

        this.updateSections(parsed?.sections);

        this.setMessageOnAllWrappers(window.cartStrings.discount_applied_successfully);

        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'discount-apply',
        });
      } else {
        this.setMessageOnAllWrappers(window.cartStrings.invalid_discount_code);
      }
    } catch {
      this.setMessageOnAllWrappers(window.cartStrings.could_not_apply_discount);
    } finally {
      const { button } = this.getEls();
      if (button) button.disabled = false;
    }
  }

  private async removeDiscount(codeToRemove: string) {
    const { input, button } = this.getEls();
    if (!input || !button) return;

    try {
      button.disabled = true;
      this.setMessage(window.cartStrings.removing_discount);

      const cfg = fetchConfig('javascript') as ReturnType<typeof fetchConfig> & {
        headers: Record<string, string>;
        body?: FormData;
      };
      const headers = cfg.headers as Record<string, string>;
      headers['X-Requested-With'] = 'XMLHttpRequest';
      delete headers['Content-Type'];

      const existingCodes = this.getAppliedDiscountCodes();

      const remainingCodes = existingCodes.filter((code) => code !== codeToRemove.toUpperCase().trim());

      const form = new FormData();
      form.append('discount', remainingCodes.join(','));
      form.append('sections', this.buildSectionsParam());
      form.append('sections_url', window.location.pathname);
      cfg.body = form;

      const resp = await fetch(`${window.routes.cart_update_url}`, cfg as RequestInit);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const text = await resp.text();
      let parsed: { sections?: SectionsPayload } | undefined;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error('Invalid response format');
      }

      this.updateSections(parsed?.sections);
      await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'discount-remove' });

      this.setMessageOnAllWrappers(window.cartStrings.discount_removed_successfully);
    } catch {
      this.setMessageOnAllWrappers(window.cartStrings.could_not_remove_discount);
    } finally {
      const { button } = this.getEls();
      if (button) button.disabled = false;
    }
  }

  private updateSections(sections?: SectionsPayload) {
    if (!sections) return;
    const footer = document.getElementById('main-cart-footer');
    const footerSectionId = footer?.getAttribute('data-id');
    const getSectionInnerHTML = (html: string | undefined, selector: string): string | null => {
      if (!html || !selector) return null;
      try {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const el = parsed.querySelector(selector);
        return el?.innerHTML || null;
      } catch {
        return null;
      }
    };

    // Cart drawer: do not mutate here; let cart-drawer-items subscriber handle it on publish

    // Icon bubble
    if (sections['cart-icon-bubble']) {
      const target = document.getElementById('cart-icon-bubble') as HTMLElement | null;
      const sectionHTML = getSectionInnerHTML(sections['cart-icon-bubble'], '.shopify-section');
      if (target && sectionHTML) target.innerHTML = sectionHTML;
    }

    // Main cart items
    if (sections['custom-main-cart-items']) {
      const target = document.querySelector('#custom-main-cart-items .js-contents') as HTMLElement | null;
      const sectionHTML = getSectionInnerHTML(sections['custom-main-cart-items'], '.js-contents');
      if (target && sectionHTML) target.innerHTML = sectionHTML;
    }

    // Main cart footer (either by handle or runtime id)
    const footerKey = footerSectionId && sections[footerSectionId] ? footerSectionId : 'custom-main-cart-footer';
    if (footerKey && sections[footerKey]) {
      const target = document.querySelector('#main-cart-footer .js-contents') as HTMLElement | null;
      const sectionHTML = getSectionInnerHTML(sections[footerKey], '.js-contents');
      if (target && sectionHTML) target.innerHTML = sectionHTML;
    }
  }

  // No drawer rebinding here; rely on drawer/cart-items to manage their own bindings after updates
}

if (!customElements.get('cart-discount-code')) {
  customElements.define('cart-discount-code', CartDiscountCode);
}
